// Layouts/MediaLibrary/MediaBrowser.tsx
// The shared grid: search, filter, upload (drag & drop), pagination.
// Used by the Media Library page (mode="manage") and by the picker dialog (mode="select").
import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import {
  MediaItem, MEDIA_ACCEPT, MEDIA_ALLOWED_EXTS, MEDIA_MAX_FILE_SIZE,
  KIND_STYLE, formatMediaSize,
} from './mediaTypes';

const PAGE_LIMIT = 24;

type TypeFilter = '' | 'image' | 'pdf' | 'word' | 'excel';
type UsageFilter = '' | 'used' | 'unused';

export interface MediaBrowserHandle {
  /** Re-fetch page 1 (e.g. after a rename/delete in the details panel) */
  refresh: () => void;
  /** Patch one item in place without refetching */
  patchItem: (item: MediaItem) => void;
}

interface MediaBrowserProps {
  mode: 'manage' | 'select';
  /** manage mode: called when a card is clicked */
  onOpen?: (item: MediaItem) => void;
  /** select mode */
  selectedIds?: string[];
  onToggle?: (item: MediaItem) => void;
  /** select mode: how many more files may still be picked (0 = none) */
  remainingSlots?: number;
  /** select mode: files just uploaded here (new or duplicate) — the picker auto-selects them */
  onUploaded?: (items: MediaItem[]) => void;
  /** manage mode: keeps the highlighted card in sync with the open details panel */
  activeId?: string | null;
  /** called whenever the total changes (header counters) */
  onTotalChange?: (total: number) => void;
}

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      padding: '6px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600,
      border: `1px solid ${active ? '#0f3d3e' : '#e2e8f0'}`,
      background: active ? '#0f3d3e' : 'white',
      color: active ? 'white' : '#475569',
    }}
  >
    {children}
  </button>
);

const MediaBrowser = forwardRef<MediaBrowserHandle, MediaBrowserProps>(({
  mode, onOpen, selectedIds = [], onToggle, remainingSlots = 0, onUploaded, activeId, onTotalChange,
}, ref) => {
  const toast = useRef<Toast>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef(0);

  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [type, setType] = useState<TypeFilter>('');
  const [usage, setUsage] = useState<UsageFilter>('');

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const fetchPage = useCallback(async (targetPage: number, append: boolean) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page: targetPage, limit: PAGE_LIMIT };
      if (query) params.search = query;
      if (type) params.type = type;
      if (usage) params.usage = usage;

      const res = await apiClient.get('/v1/media', { params });
      if (reqId !== requestRef.current) return;
      if (res.data.success) {
        setItems((prev) => (append ? [...prev, ...res.data.data] : res.data.data));
        setTotal(res.data.total ?? 0);
        setPage(targetPage);
      } else {
        setError('Failed to load the media library');
      }
    } catch (err: any) {
      if (reqId !== requestRef.current) return;
      setError(err.response?.data?.error || 'Failed to load the media library');
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [query, type, usage]);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);
  useEffect(() => { onTotalChange?.(total); }, [total, onTotalChange]);

  useImperativeHandle(ref, () => ({
    refresh: () => fetchPage(1, false),
    patchItem: (item) => setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, ...item } : i))),
  }), [fetchPage]);

  const onSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setQuery(value.trim()), 350);
  };

  // ── Upload ───────────────────────────────────────────────────────────────
  const uploadFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0 || uploading) return;

    const problems: string[] = [];
    const valid = files.filter((f) => {
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      if (!MEDIA_ALLOWED_EXTS.includes(ext)) { problems.push(`${f.name}: type not allowed`); return false; }
      if (f.size > MEDIA_MAX_FILE_SIZE) { problems.push(`${f.name}: larger than 10 MB`); return false; }
      return true;
    });
    if (problems.length) {
      toast.current?.show({ severity: 'warn', summary: 'Some files skipped', detail: problems.join(' · '), life: 5000 });
    }
    if (valid.length === 0) return;
    if (valid.length > 10) {
      toast.current?.show({ severity: 'error', summary: 'Too many files', detail: 'Upload at most 10 files at a time', life: 4000 });
      return;
    }

    const payload = new FormData();
    valid.forEach((f) => payload.append('files[]', f));

    setUploading(true);
    setProgress(0);
    try {
      const res = await apiClient.post('/v1/media', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
        onUploadProgress: (e) => setProgress(Math.round((e.loaded * 100) / (e.total ?? 1))),
      });
      const uploaded: MediaItem[] = res.data.data?.items ?? [];
      const errs: string[] = res.data.data?.errors ?? [];
      const dupes = uploaded.filter((i) => i.duplicate).length;
      const fresh = uploaded.length - dupes;

      toast.current?.show({
        severity: 'success',
        summary: fresh > 0 ? `${fresh} uploaded` : 'Already in library',
        detail: dupes > 0 ? `${dupes} file${dupes > 1 ? 's were' : ' was'} already in the library — reused, not uploaded again.` : undefined,
        life: 4500,
      });
      if (errs.length) toast.current?.show({ severity: 'warn', summary: 'Some files failed', detail: errs.join(' · '), life: 6000 });

      // show the new ones on top; duplicates move to the top too so the admin sees which one was reused
      setItems((prev) => {
        const ids = new Set(uploaded.map((u) => u.id));
        return [...uploaded, ...prev.filter((p) => !ids.has(p.id))];
      });
      setTotal((t) => t + fresh);
      onUploaded?.(uploaded);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error', summary: 'Upload failed',
        detail: err.response?.data?.error || 'Could not upload the files', life: 5000,
      });
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const hasFilters = !!(query || type || usage);
  const hasMore = items.length < total;
  const selected = new Set(selectedIds);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
      style={{ position: 'relative' }}
    >
      <Toast ref={toast} />

      {dragOver && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20, borderRadius: '14px', background: 'rgba(15,61,62,0.92)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white',
          border: '3px dashed rgba(212,241,126,0.8)', pointerEvents: 'none',
        }}>
          <i className="pi pi-cloud-upload" style={{ fontSize: '2.6rem', marginBottom: '10px' }} />
          <div style={{ fontWeight: 700 }}>Drop files to upload</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginTop: '4px' }}>PDF, Word, Excel, JPG, PNG · up to 10 MB each</div>
        </div>
      )}

      {/* Toolbar */}
      <div style={{ background: 'white', border: '1px solid #eef0f0', borderRadius: '14px', padding: '16px', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <GlobalInputField value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search files by name…" icon="pi pi-search" />
          </div>
          <input ref={fileInput} type="file" multiple accept={MEDIA_ACCEPT} style={{ display: 'none' }} onChange={(e) => e.target.files && uploadFiles(e.target.files)} />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            style={{
              padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: uploading ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg, #0f3d3e, #1a5555)', color: 'white', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap',
              opacity: uploading ? 0.7 : 1,
            }}
          >
            <i className={`pi ${uploading ? 'pi-spin pi-spinner' : 'pi-upload'}`} style={{ marginRight: '8px' }} />
            {uploading ? `Uploading ${progress}%` : 'Upload files'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {([['', 'All'], ['image', 'Images'], ['pdf', 'PDF'], ['word', 'Word'], ['excel', 'Excel']] as [TypeFilter, string][]).map(([v, label]) => (
            <Pill key={v || 'all'} active={type === v} onClick={() => setType(v)}>{label}</Pill>
          ))}
          {mode === 'manage' && (
            <>
              <span style={{ width: '1px', height: '20px', background: '#e5e7eb', margin: '0 4px' }} />
              {([['', 'Any usage'], ['used', 'In use'], ['unused', 'Unused']] as [UsageFilter, string][]).map(([v, label]) => (
                <Pill key={v || 'any'} active={usage === v} onClick={() => setUsage(v)}>{label}</Pill>
              ))}
            </>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#64748b' }}>
            {loading && items.length === 0 ? 'Loading…' : `${total} file${total !== 1 ? 's' : ''}`}
            {hasFilters && (
              <button
                type="button"
                onClick={() => { setSearch(''); setQuery(''); setType(''); setUsage(''); }}
                style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#0f3d3e', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}
              >
                Clear filters
              </button>
            )}
          </span>
        </div>

        {uploading && (
          <div style={{ background: '#e9ecef', borderRadius: '8px', height: '6px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #0f3d3e, #1a5555)', transition: 'width 0.3s ease' }} />
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#b91c1c', fontSize: '0.82rem', marginBottom: '16px' }}>
          <i className="pi pi-exclamation-circle" style={{ marginRight: '6px' }} />{error}
        </div>
      )}

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0' }}>
          <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: '56px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #e0e0e0' }}>
          <i className="pi pi-images" style={{ fontSize: '2.5rem', color: '#ddd', marginBottom: '12px', display: 'block' }} />
          <h3 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.95rem', fontWeight: 600 }}>
            {hasFilters ? 'No files match' : 'No files yet'}
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.82rem' }}>
            {hasFilters ? 'Try changing or clearing the filters' : 'Upload files or drag them here — you will only ever upload each file once'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '14px' }}>
            {items.map((item) => {
              const k = KIND_STYLE[item.type] || KIND_STYLE.other;
              const isSelected = selected.has(item.id);
              const blocked = mode === 'select' && !isSelected && remainingSlots <= 0;
              const isActive = mode === 'manage' && activeId === item.id;

              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  title={blocked ? 'Maximum files reached' : item.title}
                  onClick={() => { if (blocked) return; mode === 'select' ? onToggle?.(item) : onOpen?.(item); }}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !blocked) { e.preventDefault(); mode === 'select' ? onToggle?.(item) : onOpen?.(item); } }}
                  style={{
                    position: 'relative', background: 'white', borderRadius: '12px', overflow: 'hidden', cursor: blocked ? 'not-allowed' : 'pointer',
                    border: `2px solid ${isSelected || isActive ? '#0f3d3e' : '#eef0f0'}`,
                    boxShadow: isSelected ? '0 0 0 3px rgba(15,61,62,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
                    opacity: blocked ? 0.5 : 1, transition: 'box-shadow .15s, border-color .15s',
                  }}
                >
                  {/* thumbnail */}
                  <div style={{ height: '118px', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {item.type === 'image' ? (
                      <img src={item.url} alt={item.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ textAlign: 'center', color: k.color }}>
                        <i className={`pi ${k.icon}`} style={{ fontSize: '2.4rem' }} />
                        <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.6px', marginTop: '6px' }}>{item.ext.toUpperCase()}</div>
                      </div>
                    )}
                  </div>

                  {mode === 'select' && (
                    <div style={{
                      position: 'absolute', top: '8px', left: '8px', width: '22px', height: '22px', borderRadius: '6px',
                      background: isSelected ? '#0f3d3e' : 'rgba(255,255,255,0.92)', border: `2px solid ${isSelected ? '#0f3d3e' : '#cbd5e1'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                    }}>
                      {isSelected && <i className="pi pi-check" style={{ fontSize: '0.7rem' }} />}
                    </div>
                  )}

                  <div style={{ padding: '10px 12px 12px' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a1a1a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px', gap: '6px' }}>
                      <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{formatMediaSize(item.size)}</span>
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: '999px', whiteSpace: 'nowrap',
                        background: item.usage.total > 0 ? '#dcfce7' : '#f3f4f6',
                        color: item.usage.total > 0 ? '#15803d' : '#6b7280',
                      }}>
                        {item.usage.total > 0 ? `Used ×${item.usage.total}` : 'Unused'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => fetchPage(page + 1, true)}
              disabled={loading}
              style={{ display: 'block', margin: '18px auto 0', padding: '10px 28px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '10px', color: '#0f3d3e', fontSize: '0.82rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? <><i className="pi pi-spin pi-spinner" style={{ marginRight: '6px' }} />Loading…</> : <><i className="pi pi-angle-down" style={{ marginRight: '6px' }} />Load more</>}
            </button>
          )}
        </>
      )}
    </div>
  );
});

MediaBrowser.displayName = 'MediaBrowser';
export default MediaBrowser;
